import * as THREE from 'three'
import type { ImportedModelInstancedMeshUnit } from '../mesh-unit'
import { cloneOwnedMaterial } from '../../../material'
import { ImportedModelSelectionGeometryTemplate } from '../../interaction/selection-rendering'

/**
 * 单个 imported-model 实例槽的 Selected 覆盖对象。
 *
 * 设计说明：
 * 1. 覆盖对象直接复用正式 InstancedMesh 的 geometry，不再使用拓扑 pick host 的独立网格；
 * 2. 覆盖对象仍通过单槽 InstancedMesh 绘制，并持续复用正式 Unit 已提交的 Float32 实例矩阵；
 * 3. 覆盖对象拥有 instanceMatrix 和 Selected 材质副本，不拥有正式 geometry 或材质模板；
 * 4. 该对象禁止参与射线拾取，业务选择仍回映射到原始 source Mesh。
 */
export class ImportedModelInstancedSelectionOverlayMesh extends THREE.InstancedMesh {
  /** 供场景诊断识别实例 Selected 覆盖对象的稳定标记。 */
  public readonly isImportedModelInstancedSelectionOverlay = true

  /** 当前持续提供实例槽矩阵的正式 Unit。 */
  private sourceUnit: ImportedModelInstancedMeshUnit | null = null

  /** 当前持续联动的 source Mesh。 */
  private sourceMesh: THREE.Mesh | null = null

  /** 覆盖对象是否已经结束生命周期。 */
  private disposed = false
  /** 随覆盖对象销毁的独占材质，用其 dispose 回收 RenderObject 的绑定。 */
  private readonly ownedSelectionMaterial: THREE.Material
  /** 只有精确 primitive 子集才拥有派生 geometry。 */
  private readonly geometryTemplate = new ImportedModelSelectionGeometryTemplate()

  /**
   * 创建单槽实例 Selected 覆盖对象。
   *
   * @param unit 正式接管 source Mesh 显示职责的实例 Unit。
   * @param sourceMesh 当前需要高亮的 occurrence source Mesh。
   * @param selectedMaterial 整模型共享 Selected 配方，构造时克隆独占副本。
   * @param primitiveIndices 精确 group 子集；未指定时借用完整正式几何。
   */
  public constructor(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    selectedMaterial: THREE.Material,
    primitiveIndices?: readonly number[],
  ) {
    const ownedSelectionMaterial = cloneOwnedMaterial(selectedMaterial)
    super(unit.object.geometry, ownedSelectionMaterial, 1)
    this.ownedSelectionMaterial = ownedSelectionMaterial
    this.name = `imported-instanced-selection-overlay:${sourceMesh.uuid}`
    this.matrixAutoUpdate = false
    this.matrix.identity()
    this.renderOrder = Math.max(unit.object.renderOrder, 5)
    this.layers.mask = unit.object.layers.mask
    this.frustumCulled = false
    this.castShadow = false
    this.receiveShadow = false
    /**
     * 复用 imported-model 派生对象过滤协议，标记只在本对象内部维护。
     */
    this.userData.skipSelection = true
    this.userData.skipSelectionSubtree = true
    this.userData.isImportedModelOverlay = true
    try {
      this.syncFromSourceUnit(unit, sourceMesh, primitiveIndices)
    } catch (error) {
      this.dispose()
      throw error
    }
  }

  /**
   * 从正式实例 Unit 同步共享 geometry、渲染属性和精确实例槽矩阵。
   *
   * @param unit 当前正式实例 Unit。
   * @param sourceMesh 当前 occurrence 的原始显示 Mesh。
   * @param primitiveIndices 本轮精确 group 子集，避免扩大 Body 范围。
   * @throws source Mesh 已不属于 Unit 时抛出错误，禁止继续展示过期高亮。
   */
  public syncFromSourceUnit(
    unit: ImportedModelInstancedMeshUnit,
    sourceMesh: THREE.Mesh,
    primitiveIndices?: readonly number[],
  ): void {
    if (this.disposed) {
      throw new Error(`实例选中覆盖对象已释放：${this.name}`)
    }
    if (!unit.resolveOccurrenceRoot(sourceMesh)) {
      throw new Error(`实例选中覆盖对象无法解析 source Mesh 所属 occurrence：${sourceMesh.uuid}`)
    }
    if (this.sourceUnit && this.sourceMesh && (this.sourceUnit !== unit || this.sourceMesh !== sourceMesh)) {
      this.sourceUnit.detachInstanceMatrixProjection(this.sourceMesh, this)
    }
    // 同一 Unit 的矩阵投影已由变化槽回调维护，重复视觉刷新不能重复上传实例矩阵。
    if ((this.sourceUnit !== unit || this.sourceMesh !== sourceMesh) && !unit.attachInstanceMatrixProjection(sourceMesh, this)) {
      throw new Error(`实例选中覆盖对象无法解析 source Mesh 槽位：${sourceMesh.uuid}`)
    }
    this.sourceUnit = unit
    this.sourceMesh = sourceMesh
    /**
     * geometry 可能随实例投影重建而替换；缓存 Overlay 必须跟随当前 Unit 更新资源引用。
     */
    this.geometry = this.geometryTemplate.resolve(unit.object.geometry, primitiveIndices)
    if (this.geometry === unit.object.geometry) this.material = this.ownedSelectionMaterial
    else if (!Array.isArray(this.material)) this.material = [this.ownedSelectionMaterial]
    this.renderOrder = Math.max(unit.object.renderOrder, 5)
    this.layers.mask = unit.object.layers.mask
    this.visible = true
  }

  /**
   * 禁止视觉覆盖对象参与射线拾取。
   *
   * @param _raycaster 当前射线检测器。
   * @param _intersects 命中结果数组。
   */
  public override raycast(
    _raycaster: THREE.Raycaster,
    _intersects: THREE.Intersection[],
  ): void {
    // 实例 Selected 覆盖层只负责绘制，拾取继续由正式 InstancedMesh 回映射 source Mesh。
  }

  /** 幂等移除覆盖对象并释放其独占 instanceMatrix/GPU 状态。 */
  public disposeOverlay(): void {
    this.dispose()
  }

  /** 标准 Three.js 出口与业务清理出口保持一致，不能只释放实例缓冲而遗留材质绑定。 */
  public override dispose(): void {
    if (this.disposed) {
      return
    }
    this.disposed = true
    if (this.sourceUnit && this.sourceMesh) {
      this.sourceUnit.detachInstanceMatrixProjection(this.sourceMesh, this)
    }
    this.sourceUnit = null
    this.sourceMesh = null
    this.removeFromParent()
    try {
      super.dispose()
    } finally {
      this.ownedSelectionMaterial.dispose()
      this.geometryTemplate.dispose()
    }
  }
}
